import React, {useEffect, useState} from "react";
import type { ContextProps } from '../../../../types';
import {PageTitle} from "../../../../Components/Landmarks";
import {LayoutFullBleed} from "../../../../Components/Layout";
import {Button, inputStyles, TextField} from "../../../../Components/Inputs";
import {GridCell, gridConstants, GridContainer} from "../../../../Components/Grid";
import {useInput} from "../../../../Components/Inputs/inputUtility";
import {LinkButton, EmailLink, ExternalLink} from "../../../../Components/Link";
import {locations} from "../../../../Utilities/Location";
import {allow, Allow, policyEvents} from "../../../../Components/Authorize";
import {isArrayNullOrEmpty} from "../../../../Utilities/Types/arrayUtilities";
import {isObjectNullOrEmpty} from "../../../../Utilities/Types/objectUtilities";
import {loanLibraryCatalogApi} from "./loanLibraryCatalogApi";
import {LoanLibrarySearchResultsView} from "./LoanLibrarySearchResultsView";
import {emptyGuid} from "../../../../Utilities/commonUtilities";
import * as PolicyEvents from "../../../../Components/Authorize/policyEventsConstants";
import {loanLibraryQueueApi} from "../LoanLibraryQueue/loanLibraryQueueApi";
import {SYSTEM_TYPES} from "../../libraryConstants";

export const LoanLibraryCatalogContainer = ({
                                                handleApiCall,
                                                handleSetLoanLibraryCatalogSearchCriteria,
                                                loanLibraryCatalogSearchCriteria,
                                                redirect,
                                            }: ContextProps) => {

    const [catalogResources, setCatalogResources] = useState<Record<string, unknown>[]>([]);
    const [totalRecords, setTotalRecords] = useState(0);

    const keywordSearch = useInput("");
    const catalogNumberSearch = useInput("");

    const canManageCatalog = allow(PolicyEvents.MANAGE_LOAN_LIBRARY_CATALOG);

    const loadCatalogResourcesResult = (result: { loanLibraryCatalogItems?: Record<string, unknown>[]; totalRecords?: number }) => {
        setCatalogResources(result.loanLibraryCatalogItems || []);
        setTotalRecords(result.totalRecords || 0);
    };

    const getCatalogResources = () => {
        if (isObjectNullOrEmpty(loanLibraryCatalogSearchCriteria)) return;

        const updatedCriteria = {
            ...loanLibraryCatalogSearchCriteria,
            searchTerm: keywordSearch.value,
            catalogNumber: catalogNumberSearch.value,
            loadSearchResults: true,
        };
        handleApiCall(
            () => loanLibraryCatalogApi.getLoanLibraryCatalogResources(updatedCriteria),
            loadCatalogResourcesResult
        );
    };

    const handleEdit = (catalogItemId: string) => {
        redirect(locations.LOAN_LIBRARY_CATALOG_ITEM.getLink([catalogItemId, SYSTEM_TYPES.LoanLibrary]).to);
    };

    const handleAddToQueue = (catalogItemId: string) => {
        handleApiCall(
            () => loanLibraryQueueApi.addToQueue(catalogItemId),
            () => {
                getCatalogResources();
            }
        );
    };

    const handleSearch = () => {
        if (isObjectNullOrEmpty(loanLibraryCatalogSearchCriteria)) return;
        getCatalogResources();
    };

    const noResults = isArrayNullOrEmpty(catalogResources);

    useEffect(() => {
        if (loanLibraryCatalogSearchCriteria?.loadSearchResults) {
            getCatalogResources();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loanLibraryCatalogSearchCriteria, keywordSearch.value]);

    useEffect(() => {
        if (!isObjectNullOrEmpty(loanLibraryCatalogSearchCriteria)) {
            getCatalogResources();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [keywordSearch.value]);

    if (!loanLibraryCatalogSearchCriteria) return null;

    return (
        <>
            <PageTitle h1="Loan Library Catalog"/>

            <GridContainer>
                <GridCell>
                    <p>
                        Loan Library materials vary in length of loan. All items are only available for trial for data
                        collection.
                    </p>
                    <ul>
                            <li>Assessment materials such as WRAT 5 and KeyMath may be checked out for 60 days.</li>
                            <li>Professional materials such as Foundations of Education or the Art and Science of
                                Teaching Orientation and Mobility may be checked out for 90 days.
                            </li>
                            <li> Assistive Technology may be checked out for 60 days for trial purposes. If you require
                                support or training on how to use a device, please contact <EmailLink
                                 address="information@fimcvi.org" className="-email"/>.
                            </li>
                            <li>Smart Braillers, embossers, and Pollys may be checked out until the end of the school
                                year.
                            </li>
                            <li>Extensions may be available upon request.</li>
                    </ul>

                        <p>Unsure which video magnifier or refreshable braille display to trial? Check out
                            the <ExternalLink label={`video
                            magnifier comparison chart`}
                                              url={`https://hillsborough.sharepoint.com/:x:/s/FIMC-VI_ProfDev-WebsiteLive/ESIYjYuyPZtElQlRA_I9rfQB2-_Gi_wp-d83YJDZRuSUOg?e=av5JfG&isSPOFile=1&xsdata=MDV8MDJ8cm9kZXJpY2tAZmNpbS5vcmd8ZDI4ZTkyMWFlODEyNGNhOThhYmMwOGRlMzc0MmI3NTZ8YTM2NDUwZWJkYjA2NDJhNzhkMWIwMjY3MTlmNzAxZTN8MHwwfDYzOTAwODk1Njg3NDk5NDQwOXxVbmtub3dufFRXRnBiR1pzYjNkOGV5SkZiWEIwZVUxaGNHa2lPblJ5ZFdVc0lsWWlPaUl3TGpBdU1EQXdNQ0lzSWxBaU9pSlhhVzR6TWlJc0lrRk9Jam9pVFdGcGJDSXNJbGRVSWpveWZRPT18MHx8fA%3d%3d&sdata=NnNMdUQ5eEdXUjRGdDl4MWRJaG5xSXAxY0hTbnBEdTgxb09QU2NUaVNqcz0%3d`}/> and
                            the <ExternalLink label={`braille display comparison chart`}
                                              url={`https://hillsborough.sharepoint.com/:x:/s/FIMC-VI_ProfDev-WebsiteLive/EShV1hAXozJLow5i_AWZxqIBFYcGHoH6W0PTfNLAEkLlpQ?e=hBKSRz&isSPOFile=1&xsdata=MDV8MDJ8cm9kZXJpY2tAZmNpbS5vcmd8ZDI4ZTkyMWFlODEyNGNhOThhYmMwOGRlMzc0MmI3NTZ8YTM2NDUwZWJkYjA2NDJhNzhkMWIwMjY3MTlmNzAxZTN8MHwwfDYzOTAwODk1Njg3NTAxODQ4NXxVbmtub3dufFRXRnBiR1pzYjNkOGV5SkZiWEIwZVUxaGNHa2lPblJ5ZFdVc0lsWWlPaUl3TGpBdU1EQXdNQ0lzSWxBaU9pSlhhVzR6TWlJc0lrRk9Jam9pVFdGcGJDSXNJbGRVSWpveWZRPT18MHx8fA%3d%3d&sdata=MjJIdm81TkNEUG52MGxEZFBXVVI2MHkxMGljU0hLcTdGMnI4d21SNGxNaz0%3d`}/> to
                            view the variety of
                            features on devices that are available for trial through our Loan Library.

                            Want more instructional resources? Visit the <ExternalLink
                                label={`Instructional Resources page`}
                                url={`https://www.fimcvi.org/fimc-viresources/instructional-resources`}/>
                            that lists each ECC area with many, many resources!</p>

                        <p>Want more assessment resources? Visit the <ExternalLink label={`Assessment Resources page`}
                                                                                   url={`https://www.fimcvi.org/fimc-viresources/instructional-resources/assessment-resources`}/>!

                            If you have any questions, please contact <EmailLink label={`FIMC-VI`}
                                                                                 address="FIMC-VILoanLibrary@hcps.net"
                                                                                 className="-email"/>.</p>
                   
                </GridCell>
            </GridContainer>

            <div className="searchBox margin--bottom">
                <div className="searchBox__title">Search the catalog</div>
                <GridContainer hasMarginX hasMarginY className="searchBox__input">
                    <TextField
                        label="Keyword Search box"
                        labelClass="show-for-sr"
                        large_columns={gridConstants.column.TEN}
                        medium_columns={gridConstants.column.EIGHT}
                        name="txtSearch"
                        placeholder="Product Name, Author, or Keywords"
                        {...keywordSearch}
                    />

                    <TextField
                        label="Catalog Number Search box"
                        labelClass="show-for-sr"
                        large_columns={gridConstants.column.TEN}
                        medium_columns={gridConstants.column.EIGHT}
                        name="catalogNumberSearch"
                        placeholder="Catalog Number or Access Number"
                        {...catalogNumberSearch}
                    />

                    <GridCell
                        large_columns={gridConstants.column.TWO}
                        medium_columns={gridConstants.column.FOUR}
                    >
                        <Button
                            buttonType={inputStyles.button.types.SEARCH}
                            label="Search"
                            name="btnSearch"
                            onClick={handleSearch}
                            isPrimary
                        />
                    </GridCell>
                </GridContainer>
            </div>

            <GridContainer>
                <GridCell className="text-center">
                    <LinkButton {...locations.LOAN_LIBRARY_WHATS_NEW.getLink()} />
                    <LinkButton {...locations.LOAN_LIBRARY_QUEUE.getLink()} />
                    <LinkButton {...locations.LOAN_LIBRARY_HISTORY.getLink()} />
                    {canManageCatalog && (
                        <LinkButton
                            {...locations.LOAN_LIBRARY_CATALOG_ITEM.getLink([emptyGuid, SYSTEM_TYPES.LoanLibrary])}
                            label="Add New Item"
                        />
                    )}
                    <Allow policyEvent={policyEvents.MANAGE_CATALOG}>
                        <LinkButton
                            {...locations.LIBRARY_VENDOR.getLink([undefined, SYSTEM_TYPES.LoanLibrary])}
                        />
                        <LinkButton
                            {...locations.LOAN_LIBRARY_MANUFACTURER.getLink([emptyGuid, SYSTEM_TYPES.LoanLibrary])}
                            label="Manage Manufacturers"
                        />
                        <LinkButton
                            {...locations.LIBRARY_SHIPPING_LOCATIONS.getLink()}
                        />
                    </Allow>
                </GridCell>
            </GridContainer>

            <LayoutFullBleed className="catalog">
                <header>
                    <h2>Search Results</h2>
                </header>

                {catalogResources && !noResults && (
                    <LoanLibrarySearchResultsView
                        catalogResources={catalogResources}
                        catalogSearchCriteria={loanLibraryCatalogSearchCriteria}
                        canManageCatalog={canManageCatalog}
                        handleAddToQueue={handleAddToQueue}
                        handleEdit={handleEdit}
                        handleSetSearchCriteria={handleSetLoanLibraryCatalogSearchCriteria}
                        totalRecords={totalRecords}
                    />
                )}
                {noResults && (
                    <p className="text-center">
                        No results found. Please try a different search or contact
                        FIMC-VI at information@fimcvi.org if you believe this is in
                        error.
                    </p>
                )}
            </LayoutFullBleed>
        </>
    );
};

